import 'vuetify/styles';
import './easy.css';
import { createTestBearerToken } from '@src/testing/createTestBearerToken.js';
import {
  computed,
  createApp, defineComponent, ref,
  useTemplateRef,
  watch,
} from 'vue';
import { createVuetify } from 'vuetify';
import {
  VApp, VBtn, VCard, VCardText, VChip, VCol, VContainer, VDivider, VForm, VLayout, VNavigationDrawer, VRow, VSheet,
  VSkeletonLoader,
  VSpacer,
  VTextField,
} from 'vuetify/components';
import { apiUrl } from '@src/testpage/clients/ApiConsentClient.js';
import { getDesignSystemForSpId } from '@src/testpage/components/helpers/designSystem.js';
import { lifecycleToMode } from '@src/testpage/components/helpers/lifecycleToMode.js';
import { MfeLifecycleSelect } from '@src/testpage/components/MfeLifecycleSelect.js';
import { MfeServiceProviderSelect } from '@src/testpage/components/MfeServiceProviderSelect.js';
import { MfeDrawer, type LogEntry } from './components/MfeDrawer.js';

// See esbuild files: "define: { IS_DEV: 'false' }"
declare let IS_DEV: boolean;
if (IS_DEV) {
  const es = new EventSource('http://localhost:4401/live-reload');
  es.addEventListener('change', () => {
    window.location.reload();
  });
}

interface WebHistory {
  spId: string;
  lcId: string;
  lifecycle: string;
}

interface IframeMessage {
  height?: number;
  mfeConfig?: Record<string, string>;
}

interface MfePopStateEvent extends PopStateEvent {
  state: WebHistory;
}

interface MfeEvent {
  message: string;
  level: string;
  name: string;
  data?: object;
  raw?: object;
}

//
// Converts the current config to a query ?= param
//
const configToQueryString = ({ spId, lcId, lifecycle }: WebHistory) => {
  const urlQueryParams = new URLSearchParams();
  if (spId) urlQueryParams.set('spId', spId);
  if (lcId) urlQueryParams.set('lcId', lcId);
  if (lifecycle) urlQueryParams.set('lifecycle', lifecycle);
  return `?${urlQueryParams.toString()}`;
};

//
// Adds an entry to the web history
//
const addToWebHistory = ({ spId, lcId, lifecycle }: WebHistory) => {
  window.history.pushState({
    spId,
    lcId,
    lifecycle,
  }, '', configToQueryString({ spId, lcId, lifecycle }));
};

// Register vuetify components manually to decrease bundle size
// Get a list using: rg -I --trim "<(v-[a-z-]+)( |>|$)" src/testpage -or '$1' | sort -u | gsed -E 's/(^|-)([a-z])/\U\2/g' | gsed 's/$/,/g'
const vuetify = createVuetify({
  components: {
    VApp,
    VBtn,
    VCard,
    VCardText,
    VChip,
    VCol,
    VContainer,
    VDivider,
    VForm,
    VLayout,
    VNavigationDrawer,
    VRow,
    VSheet,
    VSpacer,
    VSkeletonLoader,
    VTextField,
  },
  directives: {},
});

const COMPONENTS = {
  MfeDrawer,
  MfeServiceProviderSelect,
  MfeLifecycleSelect,
};

const microfrontendTag = 'tef-coms';

const App = defineComponent({
  setup() {
    // Definitions
    const showDrawer = ref<boolean>(false);
    const spId = ref<string>();
    const lcId = ref<string>();
    const lifecycle = ref<string>();
    const logsEventsUnseenTimestamp = ref<number>(0);
    const logs = ref<LogEntry[]>([]);
    const mainScreenMessage = ref<string | undefined>('The micro frontend will render here');
    const mfeHasBeenRenderedOnce = ref<boolean>(false);
    const isMfeRendered = ref<boolean>(false);
    const microfrontendInstanceHost = useTemplateRef<VSheet>('microfrontend-instance-host');

    //
    // Logs a line/entry/event
    //
    function log(options: Omit<LogEntry, 'timestamp'>) {
      logs.value.push({
        source: 'Testpage',
        ...options,
        timestamp: Date.now(),
      });
    }

    //
    // Execute .save() on the microfrontend
    //
    async function triggerSaveOnMicrofrontend() {
      const iframe = document.querySelector('iframe');
      if (iframe) {
        try {
          await iframe.contentDocument!.querySelector(microfrontendTag)!.save();
        } catch {
          log({ level: 'error', message: 'Could not save consents. Please have a look at the DevTools.' });
        }
      }
    }

    //
    // Copy the url to the current state (central-app + MFE params) to the clipboard
    //
    async function copyUrlToClipboard() {
      let url = window.location.href;

      // CONTEXT: central-app
      // pathname: "/tef-coms/v2.24.0/easy.html"
      if (window.location.ancestorOrigins.length > 0) {
        const version = window.location.pathname.split('/')[2];
        const base = window.location.origin.replace('micro-frontend-proxy', 'central-app');
        url = `${base}/microfrontends${window.location.search}&name=${microfrontendTag}&version=${version}`;
      }

      await navigator.clipboard.writeText(url);
    }

    //
    // Spawns a microfrontend
    //
    async function createMicrofrontend(options?: WebHistory) {
      if (options?.spId) spId.value = options.spId;
      if (options?.lcId) lcId.value = options.lcId;
      if (options?.lifecycle) lifecycle.value = options.lifecycle;

      // delete the old state/mfe
      isMfeRendered.value = false;
      window.sessionStorage.clear();
      window.document.querySelectorAll('iframe').forEach((tag) => tag.remove());

      const accessToken = await createTestBearerToken({
        username: ['491797250005', 'foo@bar.de'],
        spID: spId.value,
        domainModel: [
          {
            customerAccountId: lcId.value,
            role: 'Owner',
          },
        ],
        scope: ['openid'],
      });

      // Create microfrontend tag
      const mfeTag = window.document.createElement(microfrontendTag);

      // Variable parts
      mfeTag.setAttribute('spid', spId.value!);
      mfeTag.setAttribute('lcid', lcId.value!);
      mfeTag.setAttribute('lifecycle', lifecycle.value!);

      // Derived parts
      mfeTag.setAttribute('accesstoken', accessToken);
      mfeTag.setAttribute('apiuri', apiUrl);
      mfeTag.setAttribute('mode', lifecycleToMode(lifecycle.value!));

      const designSystemConfig = getDesignSystemForSpId(spId.value!);
      const additionalResources = { head: '', body: '' };

      if (designSystemConfig.system === 'dsOne') {
        additionalResources.head = `<link rel="stylesheet" href="${designSystemConfig.uri}/variables.css" /><link rel="stylesheet" href="${designSystemConfig.uri}/components.css" />`;
        additionalResources.body = `<script src="${designSystemConfig.uri}/../bundle.js"></script>`;
      } else {
        mfeTag.setAttribute('occluri', designSystemConfig.uri);
      }

      // Create container <iframe> with all neccessary HTML/JS/CSS
      const iframe = window.document.createElement('iframe');
      const iframeSrcdoc = `
        <!doctype html>
        <html>
          <head>
            <meta charset="utf-8" />
            ${additionalResources.head}
          </head>
          <body style="overflow: hidden">
            ${additionalResources.body}
            ${mfeTag.outerHTML}
            <script type="module" src="${microfrontendTag}.js"></script>
            <script>
              function sendHeight() {
                parent.postMessage({ height: document.querySelector('${microfrontendTag}').scrollHeight }, '*');
              }
              window.addEventListener('load', sendHeight);
              new ResizeObserver(sendHeight).observe(document.querySelector('${microfrontendTag}'));
            </script>
          </body>
        </html>`;
      iframe.srcdoc = iframeSrcdoc;

      // Attach an event listener for events on the microfrontend
      iframe.addEventListener('load', () => {
        iframe.contentDocument!.addEventListener(microfrontendTag, (e: CustomEvent<MfeEvent>) => {
          // { "name": "consents/LOAD", "level": "info", "message": "loading consents successful"
          log({
            level: e.detail.level,
            message: e.detail.message,
            name: e.detail.name,
            source: microfrontendTag,
            data: e.detail.data,
            raw: e.detail,
          });

          // On "load/READY" remove all loading indicators
          if (e.detail.name === 'load/READY') {
            mfeHasBeenRenderedOnce.value = true;
            isMfeRendered.value = true;
          }
        });
      });

      (microfrontendInstanceHost.value!.$el as HTMLDivElement).appendChild(iframe);

      // Update web history
      addToWebHistory({
        spId: spId.value!,
        lcId: lcId.value!,
        lifecycle: lifecycle.value!,
      });

      // Check if we are called via microfrontend-proxy then send message about state across the iframes
      if (window !== window.parent) {
        const version = document.location.pathname.match(/\/(v[0-9.])+/); // eslint-disable-line sonarjs/prefer-regexp-exec
        // eslint-disable-next-line sonarjs/post-message
        window.parent.postMessage({
          mfeConfig: {
            name: microfrontendTag,
            version: version ? version[1] : 'latest',
            params: {
              // Mandatory Params:
              spId: spId.value!,
              lcId: lcId.value!,
              lifecycle: lifecycle.value!,
            },
          },
        }, '*');
      }
    }

    //
    // computed properties
    //
    const isCreateButtonActive = computed(() => spId.value && lcId.value && lifecycle.value);
    const unreadNotificationCount = computed(() => logs.value.filter((item) => item.timestamp > logsEventsUnseenTimestamp.value).length);
    const advancedModeUrl = computed(() => `test.html${configToQueryString({ spId: spId.value!, lcId: lcId.value!, lifecycle: lifecycle.value! })}`);

    //
    // web history listener (aka. back button etc)
    //
    window.addEventListener('popstate', (event: MfePopStateEvent) => {
      if (event.state) {
        createMicrofrontend(event.state); // eslint-disable-line @typescript-eslint/no-floating-promises
      }
    });

    //
    // Listen for messages from the iframe (send by central-app)
    //
    window.addEventListener('message', (event: MessageEvent<IframeMessage>) => { // eslint-disable-line sonarjs/post-message
      const iframe = document.querySelector('iframe');

      if (iframe) {
        // inner resize event
        if (event.data.height && event.data.height > 0) {
          iframe.style.height = `${event.data.height}px`;
          mainScreenMessage.value = undefined;
        }
      }

      // The MFE rendered as 0 pixel height => no content
      if (iframe && event.data.height === 0) {
        mainScreenMessage.value = 'The micro frontend did not render any HTML. Please see notifications!';
        isMfeRendered.value = true;
        iframe.style.height = '0';
      }
    });

    //
    // restore state from reload
    //
    const urlQueryParams = new URLSearchParams(window.location.search);
    if (urlQueryParams.size > 0) {
      const initParams = Object.fromEntries(urlQueryParams.entries()) as unknown as WebHistory;
      createMicrofrontend(initParams); // eslint-disable-line @typescript-eslint/no-floating-promises
    }

    // If the drawer is opened, reset the "unseen" notification counter
    watch(
      () => showDrawer.value,
      (currentValue: boolean) => {
        if (currentValue) {
          logsEventsUnseenTimestamp.value = Date.now();
        }
      },
    );

    // If the configuration changes, reset notification logs
    watch(
      [spId, lcId, lifecycle],
      () => { logs.value = []; },
    );

    // Return everything that is needen in the template
    return {
      advancedModeUrl,
      copyUrlToClipboard,
      createMicrofrontend,
      isCreateButtonActive,
      isMfeRendered,
      lcId,
      lifecycle,
      logs,
      logsEventsUnseenTimestamp,
      mainScreenMessage,
      mfeHasBeenRenderedOnce,
      showDrawer,
      spId,
      triggerSaveOnMicrofrontend,
      unreadNotificationCount,
    };
  },
});

const app = createApp(App).use(vuetify);

// Our own components must be registered globally so that every comp can access them.
Object.entries(COMPONENTS).forEach(([name, componentClass]) => {
  app.component(name, componentClass);
});

app.mount('#app');
